import { useMutation } from "@apollo/client";
import { SelectionGroup, YStack } from "@medbillai/ui";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { sentryService } from "../../lib/sentry";
import { type Intent, isIntent } from "./types";
import { upsertOnboardingIntentMutation } from "./utils/queries";
export type IntentOutcomes = {
  selected: {
    intent: Intent;
  };
  back: {};
};
const Outcome = tagged<IntentOutcomes>();
const INTENT_OPTIONS: ReadonlyArray<{
  intent: Intent;
  label: string;
}> = [{
  intent: "bill",
  label: "Yes, I have a bill I want reviewed"
}, {
  intent: "save",
  label: "No, but I'd like to save on future bills"
}];
export function IntentScreen({
  onDone
}: {
  onDone: OnDone<IntentOutcomes>;
}) {
  const [upsertSegmentation] = useMutation(upsertOnboardingIntentMutation);
  const select = (intent: Intent) => {
    const features = intent === "bill" ? ["FIX_BILLING_ERRORS"] : ["SAVE_FUTURE_BILLS"];
    // Fire-and-forget: the engine carries intent in-session, so this write only
    // matters for resume. Failures are non-blocking.
    void upsertSegmentation({
      variables: {
        input: {
          features
        }
      }
    }).catch(error => {
      sentryService.captureException(error);
    });
    onDone(Outcome.selected({
      intent
    }));
  };
  return <OnboardingScreen data-sentry-element="OnboardingScreen" data-sentry-component="IntentScreen" data-sentry-source-file="Intent.tsx">
      <OnboardingScreen.Nav onBackPress={() => onDone(Outcome.back())} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Intent.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Intent.tsx">
        <OnboardingScreen.Header alignment="left" title="Do you have a bill?" subtitle="Pick an option to get started:" data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Intent.tsx" />
        <YStack gap="$sp1.5" data-sentry-element="YStack" data-sentry-source-file="Intent.tsx">
          <SelectionGroup mode="radio" value="" onChange={value => {
          // Values come from the typed `INTENT_OPTIONS` array, so a non-Intent
          // value should never reach here.
          // TODO: SelectionGroup can't carry generic-typed data, so the value
          // arrives stringly-typed.
          if (!isIntent(value)) {
            sentryService.captureException(new Error(`Intent received unexpected SelectionGroup value: ${String(value)}`));
            // Don't guess an intent the user never picked — stay put.
            return;
          }
          select(value);
        }} data-sentry-element="SelectionGroup" data-sentry-source-file="Intent.tsx">
            {INTENT_OPTIONS.map(({
            intent,
            label
          }) => <SelectionGroup.Row key={intent} testID={`intent-fork.${intent}`} value={intent} label={label} />)}
          </SelectionGroup>
        </YStack>
      </OnboardingScreen.Content>
    </OnboardingScreen>;
}